import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { api, AccessResponse, MeResponse } from '../api/client';

type AuthContextType = {
  token: string | null;
  user: MeResponse['user'] | null;
  login: (emailOrUsername: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshMe: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('accessToken'));
  const [user, setUser] = useState<MeResponse['user'] | null>(null);

  const refreshMe = useCallback(async () => {
    if (!token) return;
    try {
      const res = await api.get<MeResponse>('/auth/me', token);
      setUser(res.user);
    } catch {
      setToken(null);
      setUser(null);
      localStorage.removeItem('accessToken');
    }
  }, [token]);

  useEffect(() => {
    refreshMe();
  }, [token, refreshMe]);

  const login = useCallback(async (emailOrUsername: string, password: string) => {
    const res = await api.post<AccessResponse>('/auth/login', { emailOrUsername, password });
    localStorage.setItem('accessToken', res.accessToken);
    setToken(res.accessToken);
  }, []);

  const register = useCallback(async (username: string, email: string, password: string) => {
    await api.post('/auth/register', { username, email, password });
    // Automatically login after register
    await login(username, password);
  }, [login]);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('accessToken');
  }, []);

  const value = useMemo(() => ({ token, user, login, register, logout, refreshMe }), [token, user, login, register, logout, refreshMe]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};